import { useState, useRef, useCallback, useEffect } from "react";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import Article from "./Article";
import { formatForDisplay } from './utils';

function Ratings({ statsData, onViewDetails }) {
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastId, setLastId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({
        waId: "",
        minRating: "",
        maxRating: "",
        minDate: "",
        maxDate: ""
    });
    const observer = useRef();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const PAGE_SIZE = 10;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url = `/ratings?limit=${PAGE_SIZE}${lastId ? `&last_id=${lastId}` : ""}`;
            const filtersExist = Object.values(filters).some(value => typeof value === "string" && value.trim() !== "");
            if (filtersExist && !lastId) {
                url = `/ratings?limit=${PAGE_SIZE}${filters.waId ? `&wa_id=${filters.waId}` : ""}${filters.minDate ? `&min_date=${filters.minDate}` : ""}${filters.maxDate ? `&max_date=${filters.maxDate}` : ""}${filters.minRating ? `&min_rating=${filters.minRating}` : ""}${filters.maxRating ? `&max_rating=${filters.maxRating}` : ""}`;
            }
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            if (!lastId) { // strict mode fix
                setRatings(response.data);
            }else {
                setRatings(prevItems => [...prevItems, ...response.data]);
            }
            setHasMore(response.data.length === PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [axiosPrivate, auth.accessToken, lastId, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const location = useLocation();
    const navigate = useNavigate();

    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setLastId(node.id);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const selectedArticle = statsData.find(
        article => article.link === location.pathname
    );

    const selectedArticleRef = useRef(null);

    useEffect(() => {
        if (selectedArticleRef.current) {
            selectedArticleRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, []);

    const updateFilters = (evt) => {
        const {name, value} = evt.target;
        setFilters(curr => ({...curr, [name]: value}));
        setLastId(null);
        setRatings([]);
    }

    const downloadExcel = async () => {
        try {
            const filtersExist = Object.values(filters).some(value => typeof value === "string" && value.trim() !== "");
            let url = '/ratings_csv';
            if (filtersExist) {
                url = `/ratings_csv?${filters.waId ? `&wa_id=${filters.waId}` : ""}${filters.minDate ? `&min_date=${filters.minDate}` : ""}${filters.maxDate ? `&max_date=${filters.maxDate}` : ""}${filters.minRating ? `&min_rating=${filters.minRating}` : ""}${filters.maxRating ? `&max_rating=${filters.maxRating}` : ""}`;
            }
            const response = await axiosPrivate.get(url, {headers: {Authorization: "Bearer " + auth.accessToken}});
            const href = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", 'numbers.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch(err) {
            console.log(err);
        }
    };

    return (
        <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/4">
                <div className="flex overflow-x-auto md:overflow-x-hidden md:overflow-y-auto md:max-h-screen md:h-screen md:pb-32 md:pr-4 md:grid md:grid-cols-1 gap-6 pb-4 md:pb-0">
                    {statsData.map((article, index) => (
                        <Article
                            key={index}
                            title={article.title}
                            value={article.value}
                            isObject={article.isObject}
                            isSelected={selectedArticle.title === article.title}
                            onViewDetails={() => onViewDetails(article)}
                            ref={selectedArticle.title === article.title ? selectedArticleRef : null}
                        />
                    ))}
                </div>
            </div>
            <div className="w-full md:w-3/4">
                <div className="rounded-lg border border-gray-200 bg-slate-100 p-6 sticky top-6">
                    <h2 className="text-2xl font-medium text-gray-900">{selectedArticle.title}</h2>
                    <p className="text-sm text-gray-500"></p>
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800">Ratings</h3>
                                <button
                                    onClick={downloadExcel}
                                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                >
                                    Download Excel
                                </button>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="w-full md:w-auto">
                                        <label className="block text-sm font-medium text-gray-700">Search Whatsapp Number</label>
                                        <input
                                            type="text"
                                            name="waId"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.waId}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Min Rating</label>
                                        <input
                                            type="number"
                                            name="minRating"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.minRating}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Max Rating</label>
                                        <input
                                            type="number"
                                            name="maxRating"
                                            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
                                            value={filters.maxRating}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Min Date</label>
                                        <input
                                            type="datetime-local"
                                            name="minDate"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.minDate}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Max Date</label>
                                        <input
                                            type="datetime-local"
                                            name="maxDate"
                                            className="mt-1 p-2 border border-gray-300 rounded-md"
                                            value={filters.maxDate}
                                            onChange={updateFilters}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {ratings.length > 0 ? (
                            <div className="overflow-hidden">
                                <div className="max-h-80 overflow-y-auto">
                                    <table className="min-w-full">
                                        <thead className="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    #
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Score
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    WhatsApp Number
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Date Created
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                            {ratings.map((rating, index) => {
                                                if (ratings.length === index + 1) {
                                                    return (
                                                        <tr id={rating.id} ref={lastItemRef} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {rating.score}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(rating.number.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {rating.created_at}
                                                            </td>
                                                        </tr>
                                                    )
                                                } else {
                                                    return (
                                                        <tr id={rating.id} key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {rating.score}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {formatForDisplay(rating.number.wa_id)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {rating.created_at}
                                                            </td>
                                                        </tr>
                                                    )
                                                }
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">No interactions found</div>
                        )}
                    </div>
                    <button onClick={() => navigate("/dashboard")} className="mt-4 inline-block text-blue-600 hover:underline">
                        ← Back to List
                    </button>
                </div>
            </div>
        </div>
    )
}

export default Ratings;